import H1 from "components/atoms/H1";
import Text from "components/atoms/Text";
import Layout from "components/layout";
import Breadcrumb, {
  BreadcrumbItem,
  BreadcrumbItemLink,
} from "components/molecules/Breadcrumb";
import Head from "components/molecules/Head";
import LoadingBox from "components/molecules/LoadingBox";
import Product from "components/molecules/Product";
import Grid, { GridCol, GridRow } from "components/organisms/Grid";
import ListProduct from "components/organisms/ListProduct";
import ProductSortSimple from "components/organisms/ProductSortSimple";
import Section from "components/organisms/Section";
import { BASE_URL, SITE_URL } from "constants/env";
import { SLUG } from "constants/page";
import { useSelector } from "hooks/useApp";
import usePage from "hooks/usePage";
import useWindowSize from "hooks/useWindowSize";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { requestFetchProductTypes } from "redux/actions/pages/productTypes";
import { requestFetchProductsByType } from "redux/actions/products/byType";
import { scrollIntoRef } from "utils/utils-common";
import "./index.scss";

interface ProductsMobileSimpleProps { }

const ProductsMobileSimple: React.FC<ProductsMobileSimpleProps> = (
  props: ProductsMobileSimpleProps,
) => {
  const dispatch = useDispatch();
  const dataPage = usePage(SLUG.category);
  const productPageRef = useRef<HTMLDivElement | null>(null);
  const { width } = useWindowSize();
  const slug = props.match.path.replace("/danh-muc/", "");
  const [metaData, setMetaData] = useState<HeadData>();
  const [productSortFilters, setProductSortFilters] = useState<FilterProps>({
    slug,
    pageSize: 12,
  });
  const [filters, setFilters] = useState<FilterProps | {}>({});
  const [{ smallBanner, largeBanner }, setBanner] = useState<Banner>({
    smallBanner: "",
    largeBanner: "",
  });
  const { byType } = useSelector(state => state.products);
  const headerDom = useMemo(() => document.getElementById("header"), []);
  const { data: productTypes } = useSelector(state => state.pages.productTypes);
  const ratio = useMemo(() => (width === 1024 ? "33.69140625%" : "33.75%"), [
    width,
  ]);
  const bannerUrl = useMemo(
    () =>
      width < 576
        ? `${(smallBanner && BASE_URL) || ""}${smallBanner}`
        : `${(largeBanner && BASE_URL) || ""}${largeBanner}`,
    [largeBanner, smallBanner, width],
  );

  useEffect(() => {
    dispatch(
      requestFetchProductsByType({
        ...filters,
        slug,
      }),
    );

    setProductSortFilters({
      ...filters,
      slug,
    });

    productPageRef &&
      productPageRef.current &&
      headerDom &&
      scrollIntoRef(productPageRef.current, headerDom.clientHeight);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    !productTypes && dispatch(requestFetchProductTypes());
  }, [dispatch, productTypes]);

  useEffect(() => {
    if (productTypes && !!productTypes.length) {
      let findBanner = productTypes.find(pro => pro.slug === slug);
      if (findBanner) {
        setBanner({
          smallBanner: findBanner.banner_mobile || "",
          largeBanner:
            (findBanner.meta_data && findBanner.meta_data.banner_image) || "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypes]);

  const handleChangeFilter = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    let name = target.name;
    let value = target.value;

    setFilters({
      ...filters,
      [name]: value,
    });
  };

  useEffect(() => {
    const { data } = dataPage;
    if (data) {
      const { meta_data } = data;
      if (meta_data && meta_data.translations) {
        const { translations } = meta_data;
        const { description, extra_data, keywords, title } = translations[0];
        setMetaData({
          titlePage: title,
          title,
          description,
          keywords,
          image: typeof extra_data === "string" && JSON.parse(extra_data).image,
        });
      }
    } else {
      setMetaData({
        titlePage: "Sản phẩm - Máy lọc không khí",
        title: "",
        description: "",
        keywords: "",
        image: "",
      });
    }
  }, [dataPage]);

  const {
    airPurifier: { products, totalRecords, loading: productDataLoading },
  } = byType.data;

  const handleHasCBLabel = useCallback(
    products &&
    products.some(
      (item: any) =>
        item.cashback_text !== null || item.cashback_img !== null,
    ),
    [products],
  );

  useEffect(() => {
    if (byType && byType.data && byType.data.seo) {
      const _seo = byType.data.seo;
      if (typeof _seo === "object" && _seo.length > 0) {
        const __seo = _seo[0];
        const {
          description, // "Loại 2 dàn rời desc"
          extra_data, // "{\"image\":\"\\/storage\\/upload\\/media\\/1-2.jpg\"}"
          keywords, // "Loại 2 dàn rời keywords"
          title, // "Loại 2 dàn rời title"
        } = __seo;

        let image = "";
        try {
          const extra = JSON.parse(extra_data);
          if (extra) {
            image = extra.image;
          }
        } catch (error) { }

        setMetaData({
          image,
          keywords,
          description,
          title,
          titlePage: title,
        });
      }
    }
  }, [byType]);

  return (
    <Layout>
      <Head data={metaData} />
      <Helmet>
        <link rel="canonical" href={`${SITE_URL}/danh-muc/${slug}`} />
      </Helmet>
      {bannerUrl && (
        <>
          <div
            className="category-banner"
            style={{
              background: `url("${bannerUrl}") no-repeat center 100% / cover`,
              paddingBottom: `${ratio}`,
            }}
            title="Banner máy lọc không khí"
          />
          <img className="category-banner-mobile" alt="Banner máy lọc không khí" src={bannerUrl} />
        </>
      )}
      <div ref={productPageRef}>
        <Breadcrumb>
          <BreadcrumbItemLink href="/">Trang chủ</BreadcrumbItemLink>
          <BreadcrumbItemLink href={`/danh-muc/${slug}`}>
            Sản phẩm
          </BreadcrumbItemLink>
          <BreadcrumbItem>Máy Lọc Không Khí</BreadcrumbItem>
        </Breadcrumb>
      </div>
      <Section modifiers={["gray"]}>
        <Grid>
          <div className="p-products">
            <ProductSortSimple
              sort={productSortFilters.SORT}
              count={products ? products.length : 0}
              total={products ? totalRecords : 0}
              handleChange={handleChangeFilter}
            />
            <ListProduct>
              {
                // Bổ sung H1 không hiển thị:
              }
              <H1>MÁY LỌC KHÔNG KHÍ</H1>
              {productDataLoading && <LoadingBox label="Đang tải dữ liệu..." />}
              {products && !!products.length ? (
                <GridRow>
                  {products.map((product: any, index: number) => (
                    <GridCol
                      key={index}
                      colXl={3}
                      colLg={4}
                      colMd={6}
                      colSm={6}
                    >
                      <Product
                        product={product}
                        addToCardButton={true}
                        trademarks={
                          product.trademarks && product.trademarks.length > 0
                            ? product.trademarks.map((tra: any) => tra.name)
                            : []
                        }
                        tags={
                          product.tags && !!product.tags.length
                            ? product.tags.map((tag: any, index2: number) => (
                              <div
                                key={index2}
                                className="m-product_trademark_item service"
                              >
                                <Text modifiers={["primary"]} size="sm">
                                  {tag.name}
                                </Text>
                              </div>
                            ))
                            : []
                        }
                        hasCashbackLabel={handleHasCBLabel}
                      />
                    </GridCol>
                  ))}
                </GridRow>
              ) : (
                <Text className="p3" modifiers={["center"]} weight="bold">
                  Không có sản phẩm
                </Text>
              )}
            </ListProduct>
          </div>
        </Grid>
      </Section>
    </Layout>
  );
};

export default ProductsMobileSimple;
